import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Badge,
  Box,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import React, { Suspense } from 'react';
import useScenarioMode from '../../../../../shared/hooks/useScenarioMode';
import { HealthMonitor } from '../../../../health-monitor';
import { StagingArea } from '../../../../staging';
import { StockTable } from '../../../../stock';
import BarChart from '../components/charts/BarChart';
import BarChartPorts from '../components/charts/BarChartPorts';
import KeyFiguresSmall from '../components/charts/KeyFiguresSmall';
import MapChart from '../components/charts/MapChart';
import MonthlyTable from '../components/voyage/MonthlyTable';
import QuarterlyTable from '../components/voyage/QuarterlyTable';
import StatisticsTable from '../components/voyage/StatisticsTable';
import ShipViewList from './ShipViewList';
import TbnAlert from './TbnAlert';
import VoyageList from './VoyageList';

const VoyageTable = React.lazy(
  () => import('../components/voyage/VoyageTable')
);

const TableFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 400,
    }}
  >
    <CircularProgress />
  </Box>
);

// Renders a tab label with a small spinner appended while its data is loading.
const TabLabel = ({ text, loading }) => (
  <Stack direction='row' alignItems='center' spacing={0.75}>
    <span>{text}</span>
    {loading && <CircularProgress size={14} />}
  </Stack>
);

const VesselSalesTabPanel = ({
  tabValue,
  onTabChange,
  loadingTabIndex,
  stagedCount,
  filters,
  summaryData,
  mapWidgetData,
  barChartWidgetDataETD,
  barChartWidgetDataETA,
  barChartPortWidgetDataETD,
  barChartPortWidgetDataETA,
  tableMonthlyData,
  tableQuarterlyData,
  tableStatisticsData,
  shipmentDetails,
  voyages,
  stockData,
  isStockLoading,
  isShipmentDetailsLoading,
  loadingVoyageId,
  quickFilterValues,
  searchInputRef,
  searchRaw,
  onSearchChange,
  onClearSearch,
  onEditClicked,
  onVoyageHistoryClick,
  onCancelVoyageClicked,
  onUnCancelVoyageClicked,
  onTableRowClick,
  onAddShipClick,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
  onStagingResolved,
  onScenarioDeltaChange,
}) => {
  const { isScenarioActive } = useScenarioMode();
  return (
    <>
      <Tabs
        key={isScenarioActive ? 'scenario' : 'normal'}
        value={tabValue}
        onChange={onTabChange}
        aria-label='Vessel Report Tabs'
      >
        <Tab
          label={
            <TabLabel text='Charts & KPIs' loading={loadingTabIndex === 0} />
          }
        />
        <Tab
          label={
            <TabLabel text='Vessel Table' loading={loadingTabIndex === 1} />
          }
        />
        <Tab
          label={<TabLabel text='Ship View' loading={loadingTabIndex === 2} />}
        />
        <Tab
          label={
            <TabLabel text='Stock Table' loading={loadingTabIndex === 3} />
          }
        />
        <Tab label='Health Monitor' />
        <Tab
          disabled={isScenarioActive}
          label={
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Badge badgeContent={stagedCount} color='warning' max={99}>
                Staging Area
              </Badge>
              {isScenarioActive && (
                <Tooltip title='Staging Area is disabled during scenario mode — it merges into production data'>
                  <span style={{ pointerEvents: 'auto', display: 'flex' }}>
                    <LockOutlinedIcon
                      sx={{ fontSize: 14, color: 'warning.main' }}
                    />
                  </span>
                </Tooltip>
              )}
            </Stack>
          }
        />
      </Tabs>
      <Box sx={{ mt: 3 }}>
        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Grid
                container
                spacing={3}
                sx={{ height: '480px', alignItems: 'stretch' }}
              >
                <Grid item xs={6}>
                  <KeyFiguresSmall
                    title='Voyages'
                    keyFigureType='totalEntries'
                    data={summaryData}
                  />
                </Grid>
                <Grid item xs={6}>
                  <KeyFiguresSmall
                    title='Cars'
                    keyFigureType='effectiveShippedSum'
                    data={summaryData}
                  />
                </Grid>
                <Grid item xs={6}>
                  <KeyFiguresSmall
                    title='Departure Countries'
                    keyFigureType='uniqueDepartureCountries'
                    data={summaryData}
                  />
                </Grid>
                <Grid item xs={6}>
                  <KeyFiguresSmall
                    title='Destination Countries'
                    keyFigureType='uniqueDestinationCountries'
                    data={summaryData}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={8}>
              <MapChart title='World Map' data={mapWidgetData} />
            </Grid>
            <Grid item xs={6}>
              <BarChart
                title={
                  filters.groupBy === 'destination' ? 'Arrival' : 'Departure'
                }
                subtitle={filters.groupBy === 'destination' ? 'ETA' : 'ETD'}
                data={
                  filters.groupBy === 'destination'
                    ? barChartWidgetDataETA
                    : barChartWidgetDataETD
                }
              />
            </Grid>
            <Grid item xs={6}>
              <BarChart
                title={
                  filters.groupBy === 'destination' ? 'Departure' : 'Arrival'
                }
                subtitle={filters.groupBy === 'destination' ? 'ETD' : 'ETA'}
                data={
                  filters.groupBy === 'destination'
                    ? barChartWidgetDataETD
                    : barChartWidgetDataETA
                }
              />
            </Grid>
            <Grid item xs={6}>
              <BarChartPorts
                title={
                  filters.groupBy === 'destination' ? 'Arrival' : 'Departure'
                }
                subtitle={filters.groupBy === 'destination' ? 'ETA' : 'ETD'}
                data={
                  filters.groupBy === 'destination'
                    ? barChartPortWidgetDataETA
                    : barChartPortWidgetDataETD
                }
              />
            </Grid>
            <Grid item xs={6}>
              <BarChartPorts
                title={
                  filters.groupBy === 'destination' ? 'Departure' : 'Arrival'
                }
                subtitle={filters.groupBy === 'destination' ? 'ETD' : 'ETA'}
                data={
                  filters.groupBy === 'destination'
                    ? barChartPortWidgetDataETD
                    : barChartPortWidgetDataETA
                }
              />
            </Grid>
            <Grid item xs={8}>
              <MonthlyTable data={tableMonthlyData} />
            </Grid>
            <Grid item xs={4}>
              <QuarterlyTable data={tableQuarterlyData} />
            </Grid>
            <Grid item xs={12}>
              <StatisticsTable data={tableStatisticsData} />
            </Grid>
          </Grid>
        )}
        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TbnAlert severity='info' onAddShip={onAddShipClick} />
            </Grid>
            <Grid item xs={12}>
              <Suspense fallback={<TableFallback />}>
                <VoyageTable
                  data={shipmentDetails}
                  onEditClicked={onEditClicked}
                  handleHistoryClick={onVoyageHistoryClick}
                  onCancelVoyageClicked={onCancelVoyageClicked}
                  onUnCancelVoyageClicked={onUnCancelVoyageClicked}
                  loadingVoyageId={loadingVoyageId}
                  onRowClick={onTableRowClick}
                  quickFilterValues={quickFilterValues}
                  searchInputRef={searchInputRef}
                  searchRaw={searchRaw}
                  onSearchChange={onSearchChange}
                  onClearSearch={onClearSearch}
                  isShipmentDetailsLoading={isShipmentDetailsLoading}
                />
              </Suspense>
            </Grid>
            <Grid item xs={12}>
              <VoyageList
                voyages={voyages}
                onEditClicked={onEditClicked}
                onCancelVoyageClicked={onCancelVoyageClicked}
                onUnCancelVoyageClicked={onUnCancelVoyageClicked}
                loadingVoyageId={loadingVoyageId}
                handleHistoryClick={onVoyageHistoryClick}
              />
            </Grid>
          </Grid>
        )}
        {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ShipViewList
                handleHistoryClick={onVoyageHistoryClick}
                onAddShip={onAddShipClick}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
                setSnackbarSeverity={setSnackbarSeverity}
                onScenarioDeltaChange={onScenarioDeltaChange}
              />
            </Grid>
          </Grid>
        )}
        {tabValue === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StockTable
                data={stockData}
                isStockLoading={isStockLoading}
                onScenarioDeltaChange={onScenarioDeltaChange}
                onEditClicked={onEditClicked}
              />
            </Grid>
          </Grid>
        )}
        {tabValue === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HealthMonitor />
            </Grid>
          </Grid>
        )}
        {tabValue === 5 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StagingArea onResolved={onStagingResolved} />
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default VesselSalesTabPanel;
